import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { Relationship } from "../../../../cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

/**
 * Abstract type representing a Sibling relationship  that can cover multiple legs
 */
export abstract class SiblingRelationship extends Relationship {
  constructor(relationshipType: CubiqTS.OrderRelationshipType) {
    super();
    this.relationshipType = relationshipType;
  }

  /**
   * Unordered list of all sibling leg orderRefs for this strategy order
   */
  public abstract get siblingIds(): Array<string>;

  /**
   * Unordered list of all OrderDataImpl legs for this strategy order
   */
  public abstract get siblingOrders(): Array<CubiqTS.OrderDataImpl>;
}

/**
 * Manages all orders that have implicitly-defined sibling orders - this will aggregate all legs for each
 * strategy and provide them in an enhances SIblingRelationship model object.
 */
export class SiblingRelationshipManager {
  /** Map of orderId Str to SiblingRelationship */
  private orderRelationshipMap: Map<string, GammaRelationshipImpl> = new Map();

  /** Map of strategyID to SiblingRelationship */
  private strategyRelationshipMap: Map<string, GammaRelationshipImpl> = new Map();

  /** Map of orderId Str to orderDataImpl */
  private orderIdMap: Map<string, CubiqTS.OrderDataImpl> = new Map();

  constructor() {}

  /**
   * Reset all internal state - this will not clear exisiting relationship maps
   * so any call to getSiblings() will still return originally definied sibling orders
   */
  public clear(): void {
    this.orderRelationshipMap = new Map();
    this.strategyRelationshipMap = new Map();
    this.orderIdMap = new Map();
  }

  /**
   * Get the sibling relationship model for the strategy associated with this order - the order must
   * currently have a rungId to identifiy it as a Gamma order
   * @param order
   * @returns
   */
  public getRelationship(order: CubiqTS.OrderDataImpl): SiblingRelationship {
    if (order.gammaRungId) {
      return this.addGammaLeg(order, CubiqTS.OrderRelationshipType.GAMMA);
    }
    // OCOs will require unique relationship instance for both legs as downstream code depend on values for siblingOrderId
    // if (order.siblingRelationship && order.siblingRelationship.relationshipType == CubiqTS.OrderRelationshipType.OCO) {
    //     return this.addStrategyLeg(order, CubiqTS.OrderRelationshipType.OCO);
    // }
    return null;
  }

  /**
   * For Order details we get in a list of all rungs but we not not need to monitor these being added/removed. This method
   * will create a SiblnigRelationship without requiring monitoring of changes
   * @param orders
   */
  public createDetailRelationship(orders: Array<CubiqTS.OrderDataImpl>): SiblingRelationship {
    if (orders && orders.length > 0) {
      const retRelationship: DetailGammaRelationshipImpl = new DetailGammaRelationshipImpl(orders);
      return retRelationship;
    }
    return null;
  }

  /**
   * For Find Orders we will not have any of the other rungs
   * @param orders
   */
  public createDetachedRelationship(): SiblingRelationship {
    const retRelationship: DetailGammaRelationshipImpl = new DetailGammaRelationshipImpl([]);
    return retRelationship;
  }

  /**
   * If order is a sbling leg, all references to the leg wil be removed from any associated
   * strategy
   * @param order
   */
  public removeOrder(order: CubiqTS.OrderDataImpl): void {
    if (order.siblingRelationship as GammaRelationshipImpl) {
      this.removeGammaLeg(order);
    }
  }

  /**
   * Add a single  leg to the related Strategy and return the SiblingRelationship for the
   * strategy
   * @param order
   * @returns
   */
  private addGammaLeg(
    order: CubiqTS.OrderDataImpl,
    orderRelationshipType: CubiqTS.OrderRelationshipType,
  ): SiblingRelationship {
    const orderId: string = order.orderId.toString();
    // Always remap the order as it may hae updated
    this.orderIdMap.set(orderId, order);

    let rel: GammaRelationshipImpl = this.orderRelationshipMap.get(orderId);
    if (!rel) {
      // The leg has not been processed before - first we check to see if strategy has been created
      rel = this.strategyRelationshipMap.get(order.strategyRef);
      if (!rel) {
        rel = new GammaRelationshipImpl(this.orderIdMap, orderRelationshipType);
        this.strategyRelationshipMap.set(order.strategyRef, rel);
      }
      rel.addOrderId(orderId);
      this.orderRelationshipMap.set(orderId, rel);
    }
    return rel;
  }

  /**
   * Remove the leg from any exisiting strategy relationship model. If the relationship
   * has no more legs, then we get rid of it
   * @param order
   */
  private removeGammaLeg(order: CubiqTS.OrderDataImpl): void {
    const orderId: string = order.orderId.toString();
    const relationship: GammaRelationshipImpl = this.orderRelationshipMap.get(orderId);

    if (relationship) {
      this.orderIdMap.delete(orderId);
      this.orderRelationshipMap.delete(orderId);
      relationship.removeOrderId(orderId);
      if (!relationship.hasSiblings()) {
        this.strategyRelationshipMap.delete(order.strategyRef);
      }
    }
  }
}

/**
 * A sibling strategy that hold the state for multiple legs of a sibling order - used
 * for both GAMMA orders and OCO orders
 */
export class GammaRelationshipImpl extends SiblingRelationship {
  /** All the orderId sibling IDs for the strategy */
  private _siblingIds: Array<string> = [];

  /** Global map of all sibling orders maintained by the SIblingRelationshipManager */
  private _orderMap: Map<string, CubiqTS.OrderDataImpl>;

  constructor(
    orderMap: Map<string, CubiqTS.OrderDataImpl>,
    relationshipType: CubiqTS.OrderRelationshipType,
  ) {
    super(relationshipType);
    this._orderMap = orderMap;
  }

  /**
   * Add a new leg ID to the strategy
   * @param orderId
   */
  public addOrderId(orderId: string) {
    this._siblingIds.push(orderId);
  }

  /**
   * Remove a leg ID from the strategy
   * @param orderId
   */
  public removeOrderId(orderId: string) {
    const index = this._siblingIds.indexOf(orderId);
    if (index >= 0) {
      this._siblingIds.splice(index, 1);
    }
  }

  /**
   * List of all sibling IDs
   */
  public get siblingIds(): Array<string> {
    return this._siblingIds.concat();
  }

  /**
   * List of all legs obtained by looking up the Ids in the global map - this will also return Gammma mid rungs
   */
  public get siblingOrders(): Array<CubiqTS.OrderDataImpl> {
    return this._siblingIds.map((orderId) => this._orderMap.get(orderId)).filter((order) => order);
  }

  /**
   * Does this relationship have any defined legs?
   * @returns
   */
  public hasSiblings(): boolean {
    return this._siblingIds.length > 0;
  }
}
/**
 * A sibling strategy that hold the state for multiple legs of a sibling order - used
 * for both GAMMA orders and OCO orders
 */
class DetailGammaRelationshipImpl extends SiblingRelationship {
  private _orders: Array<CubiqTS.OrderDataImpl>;

  constructor(orders: Array<CubiqTS.OrderDataImpl>) {
    super(CubiqTS.OrderRelationshipType.GAMMA);
    this._orders = orders;
  }

  /**
   * List of all sibling IDs
   */
  public get siblingIds(): Array<string> {
    return this._orders.map((order) => order.orderId.toString());
  }

  /**
   * List of all legs obtained by looking up the Ids in the global map - this will also return Gammma mid rungs
   */
  public get siblingOrders(): Array<CubiqTS.OrderDataImpl> {
    return this._orders.concat();
  }

  /**
   * Does this relationship have any defined legs?
   * @returns
   */
  public hasSiblings(): boolean {
    return this._orders.length > 0;
  }
}
